import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MultiSelect } from '@mantine/core'

import styles from '../VolunteeringFiltersModal.module.scss'
import { FilterProps } from '../VolunteeringFiltersModal.types'

import { Box, FakeCheckbox, Flex, Icon, Loader, Typography } from '@percent/lemonade'
import { getVolunteeringActivities } from '@percent/workplace-giving/api/volunteering/getVolunteeringActivities/getVolunteeringActivities'
import { useQuery } from '@percent/workplace-giving/common/hooks'

export function ActivitiesFilter({ selectedFilters, setSelectedFilters }: FilterProps) {
  const { t } = useTranslation()

  const [dropdownOpened, setDropdownOpened] = useState(false)

  const { data: activitiesData, isPending } = useQuery(['getVolunteeringActivities'], getVolunteeringActivities, {
    staleTime: 300 * 60 * 1000
  })

  const activitiesOptions = useMemo(() => {
    if (activitiesData) {
      return activitiesData
        .map(activity => ({
          value: activity.id,
          label: activity.name
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }

    return []
  }, [activitiesData])

  if (isPending) {
    return <Loader />
  }

  return (
    <Box>
      <Typography className={styles.FiltersSectionTitle}>
        {t('workplace_giving.volunteering.filters.activities.label')}
      </Typography>
      <MultiSelect
        dropdownOpened={dropdownOpened}
        comboboxProps={{ withinPortal: false }}
        wrapperProps={{ 'data-testid': 'activitiesSelect' }}
        style={{ width: '100%' }}
        maxDropdownHeight={150}
        placeholder={
          !selectedFilters.activities?.length
            ? (t('workplace_giving.volunteering.filters.activities.placeholder') as string)
            : ''
        }
        data={activitiesOptions}
        value={selectedFilters.activities as string[]}
        onChange={selectedValues => {
          setSelectedFilters({ ...selectedFilters, activities: selectedValues })
        }}
        leftSectionProps={{
          onClick: () => {
            setDropdownOpened(!dropdownOpened)
          }
        }}
        onDropdownOpen={() => setDropdownOpened(true)}
        onDropdownClose={() => setDropdownOpened(false)}
        rightSectionPointerEvents="none"
        rightSection={
          <Box className={styles.FiltersDropdownRightSectionWrapper}>
            {selectedFilters.activities?.length ? (
              <button
                type="button"
                data-testid="clearActivitiesFilter"
                onClick={() => {
                  setSelectedFilters({ ...selectedFilters, activities: [] })
                }}
              >
                <Icon name="close" size={4} color="gray600" />
              </button>
            ) : null}

            <button
              type="button"
              data-testid="toggleActivitiesFilter"
              onClick={() => {
                setDropdownOpened(!dropdownOpened)
              }}
            >
              <Icon name={dropdownOpened ? 'dropdown-arrow-up' : 'dropdown-arrow-down'} size={8} color="gray600" />
            </button>
          </Box>
        }
        renderOption={item => (
          <Flex justify="center" align="center">
            <FakeCheckbox isSelected={!!item.checked} />
            <div style={{ marginLeft: '0.5rem' }}>{item.option.label}</div>
          </Flex>
        )}
      />
    </Box>
  )
}
